import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, DollarSign, Briefcase, HardHat, Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/site/SEO";
import { COMPANY } from "@/lib/constants";
import api from "@/lib/api";

export default function CareersHub() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const origin = typeof window !== "undefined" ? window.location.origin : `https://www.cgconcretellc.com`;
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Careers at CG Concrete LLC",
      url: `${origin}/careers`,
      description: "Join the CG Concrete LLC crew in Watertown, WI. View open concrete jobs and apply online.",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: COMPANY.name,
      url: origin,
      logo: `${origin}/archive/logo.jpg`,
      telephone: COMPANY.phone,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: "Careers", item: origin + "/careers" },
      ],
    },
  ];

  return (
    <div>
      <SEO
        title="Careers — Concrete Jobs in Watertown, WI | CG Concrete LLC"
        description="Now hiring in Watertown, WI. Join CG Concrete LLC — CDL Drivers and Concrete Laborers. Competitive pay, home every night, room to grow. Apply online today."
        canonical={`${origin}/careers`}
        schema={schema}
      />

      {/* Hero */}
      <section className="relative bg-secondary text-white grain-texture overflow-hidden">
        <img src="/archive/job-trowel.webp" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/70 to-secondary" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <span className="text-primary font-bold text-sm tracking-widest">JOIN THE CREW</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-3 max-w-3xl">Build Your Career in Concrete</h1>
          <p className="text-white/75 mt-5 text-lg max-w-2xl">
            We're a locally owned Watertown company growing fast — and we're looking for dependable people who take pride in their work.
            Competitive pay, local routes, and a crew that feels like family.
          </p>
          <div className="mt-8 flex flex-wrap gap-6 text-sm">
            <span className="flex items-center gap-2 text-white/80"><MapPin className="h-4 w-4 text-primary" /> Watertown, WI &amp; surrounding areas</span>
            <span className="flex items-center gap-2 text-white/80"><Briefcase className="h-4 w-4 text-primary" /> Full-time &amp; seasonal</span>
            <span className="flex items-center gap-2 text-white/80"><DollarSign className="h-4 w-4 text-primary" /> Competitive hourly pay</span>
          </div>
        </div>
      </section>

      {/* Openings */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <h2 className="font-heading text-3xl font-extrabold">Open Positions</h2>
        <p className="text-muted-foreground mt-2">Click a role to see full details and apply online.</p>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
        ) : jobs.length === 0 ? (
          <div className="mt-8 bg-card border border-border p-12 text-center">
            <p className="text-muted-foreground">No open positions right now — but we're always glad to hear from good people.</p>
            <Button asChild className="mt-4 bg-primary rounded-sm font-bold"><a href={`tel:${COMPANY.phoneRaw}`}>Call Us: {COMPANY.phone}</a></Button>
          </div>
        ) : (
          <div className="mt-8 grid md:grid-cols-2 gap-6" data-testid="job-list">
            {jobs.map((job) => (
              <Link
                key={job.slug}
                to={`/careers/${job.slug}`}
                className="group relative bg-card border border-border p-7 hover:border-primary hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)] transition-all hover:-translate-y-1"
                data-testid={`job-card-${job.slug}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="h-12 w-12 bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors">
                    <HardHat className="h-6 w-6 text-primary group-hover:text-white transition-colors" />
                  </span>
                  <span className="text-xs font-bold uppercase tracking-wider bg-primary/10 text-primary px-2.5 py-1 rounded-sm">Hiring</span>
                </div>
                <h3 className="font-heading text-2xl font-bold mt-5">{job.title}</h3>
                <p className="text-muted-foreground mt-2 leading-relaxed">{job.tagline}</p>
                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                  <span className="flex items-center gap-1.5 font-semibold"><DollarSign className="h-4 w-4 text-primary" /> ${job.salary_min}–${job.salary_max}/hr</span>
                  <span className="flex items-center gap-1.5 text-muted-foreground"><MapPin className="h-4 w-4 text-primary" /> {job.location_city}, {job.location_state}</span>
                  <span className="flex items-center gap-1.5 text-muted-foreground"><Briefcase className="h-4 w-4 text-primary" /> {job.employment_type}</span>
                </div>
                <span className="mt-6 inline-flex items-center gap-1.5 font-bold text-primary group-hover:gap-3 transition-all">
                  View role &amp; apply <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-14 bg-secondary text-white grain-texture p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl font-bold">Don't see the right fit?</h3>
            <p className="text-white/70 mt-1">We're always interested in hardworking people. Give us a call.</p>
          </div>
          <Button asChild variant="outline" className="h-12 px-8 rounded-sm font-bold bg-transparent border-white/30 text-white hover:bg-white hover:text-secondary shrink-0">
            <a href={`tel:${COMPANY.phoneRaw}`}><Phone className="mr-2 h-4 w-4" /> {COMPANY.phone}</a>
          </Button>
        </div>
      </section>
    </div>
  );
}
